import { AlignAttributes, TemplateItem } from "types";
import { getNumberRoundedToPrecision } from "../..";
import { toServerAlignRatio } from "../../alignRatio";

/**
 * `x`/`y` are the box's linear top-left fraction. When the box width/height
 * (canvas fractions) are provided, off-canvas (negative) positions are
 * re-encoded into the renderer's far-edge `Align` convention (see
 * {@link toServerAlignRatio}); positions stay raw otherwise (e.g. mask
 * sub-regions, which are box-relative and always >= 0).
 */
export const generateAlignAttributes = (
  x: number,
  y: number,
  width?: number,
  height?: number
): AlignAttributes => {
  return {
    type: "Align",
    horizontal: "none",
    vertical: "none",
    horizontal_ratio: getNumberRoundedToPrecision(
      width !== undefined ? toServerAlignRatio(x, width) : x
    ),
    vertical_ratio: getNumberRoundedToPrecision(
      height !== undefined ? toServerAlignRatio(y, height) : y
    ),
  };
};

const isAxisCentered = (position: number, size: number): boolean =>
  getNumberRoundedToPrecision(position + size / 2) === 0.5;

export const generateCenteredAlignAttributes = (
  x: number,
  y: number,
  width: number,
  height: number
): AlignAttributes => {
  const horizontalCentered = isAxisCentered(x, width);
  const verticalCentered = isAxisCentered(y, height);
  return {
    type: "Align",
    horizontal: horizontalCentered ? "center" : "none",
    vertical: verticalCentered ? "center" : "none",
    horizontal_ratio: horizontalCentered
      ? null
      : getNumberRoundedToPrecision(toServerAlignRatio(x, width)),
    vertical_ratio: verticalCentered
      ? null
      : getNumberRoundedToPrecision(toServerAlignRatio(y, height)),
  };
};

export const generatePositionAttributes = (
  x: number,
  y: number,
  width?: number,
  height?: number
): NonNullable<TemplateItem["attributes"]>[number] => {
  return {
    type: "Position",
    position: generateAlignAttributes(x, y, width, height),
  };
};

export const generateCenteredPositionAttributes = (
  x: number,
  y: number,
  width: number,
  height: number
): NonNullable<TemplateItem["attributes"]>[number] => {
  return {
    type: "Position",
    position: generateCenteredAlignAttributes(x, y, width, height),
  };
};
